import React, {useCallback, useMemo, useState, useEffect} from 'react';
import {DataGrid} from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import {apiGet, getUserName, recordListGridTypes} from "../../utils";
import {useOutletContext} from "react-router";

export const RecordListGrid = ({type, managerPlayerMatchup, pointsOrAgainst, weekRegularFull, dirtyOrClean, topOrBottom}) => {
    const {currentSeason} = useOutletContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchRecordData = useCallback(() => {
        setLoading(true);
        let queryPath;
        if (type === recordListGridTypes.STANDARD || type === recordListGridTypes.WITH_WEEK || type === recordListGridTypes.WITH_WEEK_AND_PLAYER) {
            queryPath = `ten/${managerPlayerMatchup}/${pointsOrAgainst}/${weekRegularFull}/${topOrBottom}`;
        } else if (type === recordListGridTypes.MATCHUP_DIFF) {
            queryPath = `ten/${managerPlayerMatchup}/difference/${topOrBottom}`;
        } else if (type === recordListGridTypes.DIRTY_CLEAN) {
            queryPath = `${dirtyOrClean}/all`;
        }
        apiGet(`record/${queryPath}`, (data) => {
            setRows(data.data);
            setLoading(false);
        });
    }, [type, managerPlayerMatchup, pointsOrAgainst, weekRegularFull, dirtyOrClean, topOrBottom]);

    const columnModels = useMemo(() => {
        const pointsField = `${weekRegularFull === 'regular' ? 'reg_season_' : weekRegularFull === 'full' ? 'full_season_' : ''}${pointsOrAgainst === 'points' ? 'total_points' : 'points_against'}`;
        const common = {sortable: false, disableColumnMenu: true, headerClassName: 'grid-header'};
        const colDefs = {
            rankCol: [
                {...common, width: 60, headerName: 'Rank', field: 'rank'}
            ],
            defaultColumns: [
                {...common, width: 60, headerName: 'Year', field: 'year'},
                {...common, width: 80, headerName: 'Manager', field: 'manager_name'},
                {...common, width: 75, headerName: 'Points', field: pointsField, type: 'number', headerAlign: 'right'}
            ],
            withWeek: [
                {...common, width: 60, headerName: 'Year', field: 'year'},
                {...common, width: 60, headerName: 'Week', field: 'week'},
                {...common, width: 80, headerName: 'Manager', field: 'manager_name'},
                {...common, width: 65, headerName: 'Points', field: 'total_points', type: 'number', headerAlign: 'right'}
            ],
            withWeekAndPlayer: [
                {...common, width: 60, headerName: 'Year', field: 'year'},
                {...common, width: 60, headerName: 'Week', field: 'week'},
                {...common, width: 80, headerName: 'Manager', field: 'manager_name'},
                {...common, width: 160, headerName: 'Player', field: 'player_name'},
                {...common, width: 50, headerName: 'Pos', field: 'pos'},
                {...common, width: 65, headerName: 'Salary', field: 'salary', type: 'number', headerAlign: 'right'},
                {...common, width: 65, headerName: 'Points', field: 'points', type: 'number', headerAlign: 'right'},
                {...common, width: 70, headerName: 'Started', field: 'started', headerAlign: 'center', align: 'center',
                    renderCell: p => p.row.started ? 'Yes' : 'No'}
            ],
            opponentCols: [
                {...common, width: 80, headerName: 'Manager', field: 'opponent_name'},
                {...common, width: 65, headerName: 'Points', field: 'opp_total_points', type: 'number', headerAlign: 'right'}
            ],
            diffCol: [
                {...common, width: 100, headerName: 'Difference', field: 'diff', type: 'number', headerAlign: 'right'}
            ]
        };

        const calcWidth = (cols) => {
            return cols.reduce((t, c) => t + c.width, 0) + 2;
        };

        const defaultModel = [...colDefs.rankCol, ...colDefs.defaultColumns];
        const withWeekModel = [...colDefs.rankCol, ...colDefs.withWeek];
        const withWeekAndPlayerModel = [...colDefs.rankCol, ...colDefs.withWeekAndPlayer];
        const matchupDiffModel = [...colDefs.rankCol, ...colDefs.withWeek, ...colDefs.opponentCols, ...colDefs.diffCol];
        const dirtyCleanModel = [...colDefs.withWeek, ...colDefs.opponentCols, ...colDefs.diffCol];

        return {
            standard: {cols: defaultModel, width: calcWidth(defaultModel)},
            withWeek: {cols: withWeekModel, width: calcWidth(withWeekModel)},
            withWeekAndPlayer: {cols: withWeekAndPlayerModel, width: calcWidth(withWeekAndPlayerModel)},
            matchupDiff: {cols: matchupDiffModel, width: calcWidth(matchupDiffModel)},
            dirtyClean: {cols: dirtyCleanModel, width: calcWidth(dirtyCleanModel)}
        };
    }, [pointsOrAgainst, weekRegularFull]);

    const displayColumns = useMemo(() => {
        return columnModels[type];
    }, [type, columnModels]);

    useEffect(() => {
        fetchRecordData();
    }, [fetchRecordData]);

    const determineRowClass = (params) => {
        let classes = '';
        classes += params.row.year && params.row.year === currentSeason.year? 'current-year ' : '';
        if (type === recordListGridTypes.STANDARD || type === recordListGridTypes.WITH_WEEK || type === recordListGridTypes.WITH_WEEK_AND_PLAYER) {
            classes += params.row.manager_name === getUserName() ? 'is-self' : '';
        } else if (type === recordListGridTypes.MATCHUP_DIFF || type === recordListGridTypes.DIRTY_CLEAN) {
            classes += params.row.manager_name === getUserName() ? 'is-self' : params.row.opponent_name === getUserName() ? 'is-self' : '';
        }
        return classes;
    };

    return (
        <div style={{width: displayColumns.width, marginLeft: 10, marginRight: 10}}>
            {loading && <LinearProgress color="secondary"/>}
            <DataGrid columns={displayColumns.cols} rows={rows} hideFooter
                      columnHeaderHeight={30} rowHeight={23} autoHeight getRowId={r => r.row_id}
                      getRowClassName={(params) => (determineRowClass(params))}
                      disableRowSelectionOnClick disableMultipleRowSelection disableColumnResize/>
        </div>
    );
}
